import { switchNetwork } from 'app-lib/web3ReactUtils'
import { ProviderChangedEvent } from 'bzx-common/src/domain/events/providerEvents'
import * as mobx from 'mobx'
import { AbstractConnector } from '@web3-react/abstract-connector'
import { chainConfigs, ChainId, isChainSupported } from '../config/appConfig'
import {
  getConnectorByProviderType,
  IProviderDetails,
  providersDetails,
  ProviderType,
} from '../domain/ProviderTypeDictionary'
import hashUtils from '../lib/hashUtils'
import web3ProviderUtils from '../lib/web3ProviderUtils'
import Web3ConnectionFactory from '../services/Web3ConnectionFactory'
import RootStore from './RootStore'

interface IWeb3Connection {
  activatingProvider: ProviderType | null
  isDisconnecting: boolean
  pendingProvider: boolean
  providerIsChanging: boolean
  supportedNetwork: boolean
  connectedAddress: string
  impersonateAddress: string
}

interface IWeb3ReactExport {
  connector?: AbstractConnector
  activate: (connector: AbstractConnector) => void
  deactivate: () => void
  active: boolean
  error?: any
}

export default class Web3Connection implements IWeb3Connection {
  public rootStore: RootStore
  public supportedNetwork = true
  public providerType: ProviderType = ProviderType.None
  /** * Current connected user address*/
  public connectedAddress = ''
  public impersonateAddress = localStorage.getItem('impersonateAddress') || ''
  public providerIsChanging = false
  /** We are waiting for a provider to kick in when the app loads,  */
  public pendingProvider = true
  public storedProvider: ProviderType = ProviderType.None
  public activatingProvider: ProviderType | null = null
  public isDisconnecting = false
  public chainId?: ChainId
  public web3React?: IWeb3ReactExport

  get walletAddress() {
    return this.impersonateAddress || this.connectedAddress || ''
  }

  /**
   * Depending on the chain, we support different wallet providers.
   * eg: We add Binance wallet on BSC
   */
  get supportedWalletProviders() {
    if (!this.chainId || !isChainSupported(this.chainId)) {
      return []
    }
    return chainConfigs[this.chainId].wallets
  }

  /**
   * eg: 0x1ba2...edf
   */
  get shortWalletAddress() {
    return hashUtils.shortHash(this.walletAddress, 4, 4)
  }

  /**
   * Gives info about displayName, Logo, connector
   */
  get providerTypeDetails(): IProviderDetails {
    const providerType = this.activatingProvider || this.providerType
    return providersDetails[providerType]
  }

  get networkDetails() {
    if (this.chainId && isChainSupported(this.chainId)) {
      return chainConfigs[this.chainId].chainDetails
    }
    return null
  }

  /**
   * blockExplorer url if the current connection is a known chain; '' otherwise.
   */
  get blockExplorerUrl() {
    return this.networkDetails?.blockExplorer || ''
  }

  /**
   * Computed link to etherscan for the active account (wallet address)
   * Eg: https://etherscan.io/address/0x56d811088235F11C8920698a204A5010a788f4b3
   */
  get explorerWalletLink() {
    if (this.networkDetails && this.walletAddress) {
      return `${this.networkDetails.blockExplorer}/address/${this.walletAddress}`
    }
    return ''
  }

  /**
   * True if there is a connected provider and a defined wallet address
   */
  get isConnected() {
    return this.providerType !== ProviderType.None && !!this.walletAddress
  }

  /**
   * Unsupported network is true if the user is connected to a chain we dont support.
   * This is for multichain support.
   */
  get unsupportedNetwork() {
    return this.chainId ? !isChainSupported(this.chainId) : false
  }

  /**
   * We have a provider but no account selected yet
   */
  get isLoadingWallet() {
    return this.activatingProvider !== null && this.activatingProvider !== ProviderType.None
  }

  get hasProvider() {
    return (
      (this.activatingProvider !== null && this.activatingProvider !== ProviderType.None) ||
      this.providerType !== ProviderType.None
    )
  }

  get provider() {
    return this.rootStore.fulcrumProvider
  }

  get waitingForWallet() {
    return (
      this.chainId === undefined &&
      !!this.activatingProvider &&
      this.activatingProvider !== ProviderType.None
    )
  }

  /**
   * Helper to set values through mobx actions.
   */
  set(prop: keyof IWeb3Connection, value: any) {
    ;(this[prop] as any) = value
  }

  /**
   * Helper to assign multiple props values through a mobx action.
   */
  public assign(props: Partial<IWeb3Connection>) {
    Object.assign(this, props)
  }

  /**
   * Helper to expose web3 react context and be able to control the connection in the data layer
   * TODO: add ts type for context
   */
  public getWeb3ReactContext(context: any) {
    this.web3React = context
  }

  /**
   * Connect to Ethereum using the specified provider type
   * @param providerType MetaMask, etc...
   */
  public async connect(providerType: ProviderType) {
    if (this.providerType !== ProviderType.None) {
      await this.disconnect()
    }
    const connector = getConnectorByProviderType(providerType, this.chainId)
    this.set('activatingProvider', providerType)
    if (this.web3React && connector) {
      this.web3React.activate(connector)
    }
  }

  /**
   * Helper to activate the last used web3 connection settings by the user. (chain + wallet)
   * By default it will connect to Ethereum (chainId 1) and use our RPC.
   */
  public checkAndStartStoredProvider(web3React: IWeb3ReactExport) {
    const chainId = Number(localStorage.getItem('userChainId') || 1) as ChainId
    // providerType is saved in localStorage in web3ProviderUtils.setWeb3ProviderFinalize
    this.storedProvider = (localStorage.getItem('providerType') ||
      ProviderType.None) as ProviderType
    if (this.storedProvider !== ProviderType.None) {
      const storedConnector = getConnectorByProviderType(this.storedProvider, chainId)
      if (storedConnector) {
        this.activatingProvider = this.storedProvider
        setTimeout(() => {
          // @see https://github.com/NoahZinsmeister/web3-react/issues/78#issuecomment-936127843
          web3React.activate(storedConnector)
        }, 0)
      }
    } else {
      web3ProviderUtils.setReadonlyWeb3Provider(this.provider, chainId)
    }
    return this.storedProvider
  }

  setReadOnlyProvider(chainId: ChainId) {
    web3ProviderUtils.setReadonlyWeb3Provider(this.provider, chainId)
  }

  public switchNetwork(chainId: ChainId) {
    const { web3Connection } = this.rootStore
    if (web3Connection.isConnected) {
      switchNetwork(chainId)
    } else {
      web3Connection.setReadOnlyProvider(chainId)
    }
  }

  public async disconnect() {
    if (this.web3React && this.chainId) {
      try {
        this.set('isDisconnecting', true)
        this.web3React.deactivate()
        await web3ProviderUtils.setReadonlyWeb3Provider(this.provider, this.chainId)
      } catch (err) {
        console.error(err)
      } finally {
        this.set('isDisconnecting', false)
      }
    }
  }

  /**
   * helper to check network
   */
  private syncConnection(event: ProviderChangedEvent) {
    this.pendingProvider = false
    this.providerIsChanging = false
    this.providerType = event.providerType
    this.activatingProvider = null
    const chainId = Web3ConnectionFactory.networkId as ChainId
    const walletAddress = Web3ConnectionFactory.userAccount || ''
    if (this.connectedAddress !== walletAddress || this.chainId !== chainId) {
      // To avoid issues with mobx derivations when the user changes wallet,
      // we need to clear the current user data before updating the connection data.
      this.rootStore.clearData()
      this.connectedAddress = walletAddress
      this.chainId = chainId
    }
  }

  public init() {
    const fp = this.provider
    fp.on('ProviderIsChanging', () => this.set('providerIsChanging', true))
    fp.on('ProviderChanged', this.syncConnection)
    mobx.when(
      () => !!this.web3React,
      () =>
        setTimeout(() => {
          this.checkAndStartStoredProvider(this.web3React!)
        }, 100)
    )
  }

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore
    mobx.makeAutoObservable(this, { rootStore: false }, { autoBind: true, deep: false })
  }
}
